import React from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { subscribeToTenant, type NotificationCreatedEvent } from '../../conversations/services/conversationHub';
import { notificationService, type InAppNotification } from '../services/notificationService';

type NotificationItem = InAppNotification;

interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined);

const buildRealtimeNotification = (evt: NotificationCreatedEvent): NotificationItem => ({
  id: evt.notificationId,
  type: evt.type,
  title: evt.title,
  body: evt.body,
  linkUrl: evt.linkUrl,
  isRead: evt.isRead,
  createdAtUtc: evt.createdAtUtc || new Date().toISOString(),
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated || !auth?.tenantId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setIsLoading(true);
    try {
      const result = await notificationService.list(1, 30);
      setNotifications(result.items);
      setUnreadCount(result.unreadCount);
    } finally {
      setIsLoading(false);
    }
  }, [auth?.tenantId, isAuthenticated]);

  const refreshUnreadCount = React.useCallback(async () => {
    if (!isAuthenticated || !auth?.tenantId) {
      setUnreadCount(0);
      return;
    }

    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // keep current badge count if request fails
    }
  }, [auth?.tenantId, isAuthenticated]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    if (!isAuthenticated || !auth?.tenantId) {
      return;
    }

    let isMounted = true;
    let unsubscribe: (() => Promise<void>) | null = null;

    const connect = async () => {
      try {
        unsubscribe = await subscribeToTenant({
          tenantId: auth.tenantId,
          onConversationListItemUpdated: () => {},
          onConversationNotification: () => {},
          onNotificationCreated: (evt) => {
            if (!isMounted) return;

            setNotifications((prev) => {
              const candidate = buildRealtimeNotification(evt);
              if (prev.some(item => item.id === candidate.id)) {
                return prev;
              }
              return [candidate, ...prev].slice(0, 30);
            });

            if (!evt.isRead) {
              setUnreadCount((prev) => prev + 1);
            }
            void refreshUnreadCount();
          },
          onReconnected: async () => {
            if (!isMounted) return;
            await refresh();
          },
        });
      } catch (error) {
        console.warn('[NotificationProvider] tenant realtime subscribe failed', error);
      }
    };

    void connect();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        void unsubscribe();
      }
    };
  }, [auth?.tenantId, isAuthenticated, refresh, refreshUnreadCount]);

  const markAsRead = React.useCallback(async (notificationId: string) => {
    if (!notificationId) return;

    let wasUnread = false;
    setNotifications((prev) => prev.map((item) => {
      if (item.id !== notificationId) return item;
      if (!item.isRead) wasUnread = true;
      return { ...item, isRead: true };
    }));

    if (wasUnread) {
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    }

    await notificationService.markRead(notificationId);
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  const markAllAsRead = React.useCallback(async () => {
    setNotifications((prev) => prev.map(item => ({ ...item, isRead: true })));
    setUnreadCount(0);
    await notificationService.markAllRead();
  }, []);

  const value = React.useMemo<NotificationContextValue>(() => ({
    notifications,
    unreadCount,
    isLoading,
    refresh,
    markAsRead,
    markAllAsRead,
  }), [isLoading, markAllAsRead, markAsRead, notifications, refresh, unreadCount]);

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = (): NotificationContextValue => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
